// use-auth.ts — Auth suite, snapshot reader.
//
// Returns the current authenticated user / session in the universal QueryResult
// envelope. status=Loading during initial Supabase session restore; status=Success
// with data=null means "resolved, not authenticated"; status=Success with data set
// means "logged in". For sign-in/out flows, consume the per-action hooks
// (useSignOut, useSignInWithOtp, etc.) — see apps/fe/src/hooks/planning/async-protocol.md.

'use client';

import type { Session, User } from '@supabase/supabase-js';
import { QUERY_STATUS, type QueryResult } from '@/types/async-protocol';
import { useAuthContext } from './AuthProvider';

export interface AuthData {
  user: User;
  session: Session;
}

export function useAuth(): QueryResult<AuthData> {
  const snapshot = useAuthContext();

  if (snapshot.loading) {
    return { data: null, status: QUERY_STATUS.Loading, error: null, refetch: NOOP };
  }
  if (!snapshot.user || !snapshot.session) {
    return { data: null, status: QUERY_STATUS.Success, error: null, refetch: NOOP };
  }
  return {
    data: { user: snapshot.user, session: snapshot.session },
    status: QUERY_STATUS.Success,
    error: null,
    refetch: NOOP,
  };
}

const NOOP = (): void => undefined;
